import _extends from "@babel/runtime/helpers/extends";
import getProductId from "./getProductId";
import getProductForms from "./getProductForms";
import { FF_ACTION, FF_RULESETS } from "../constants";
/**
 * Fetch the pac actions avaiable to a particular user based on launch darkly
 * flags.
 *
 * @param {String} apiQuotingUrl
 * @param {Array.<{
 *   "pac-NAME": Array.<"action"|"rulesets">
 * }>} featureFlags - object whose keys start with `pac-` and should contain an
 *   array of strings matching feature flags in the code base
 * @param {String} policyId
 * @param {Boolean} debug
 * @returns See result of `determineUserAccessibleActions`
 */

export default function getPacActions(_ref) {
  var apiQuotingUrl = _ref.apiQuotingUrl,
      featureFlags = _ref.featureFlags,
      policyId = _ref.policyId,
      _ref$debug = _ref.debug,
      debug = _ref$debug === void 0 ? false : _ref$debug;
  return getProductId({
    apiQuotingUrl: apiQuotingUrl,
    policyId: policyId
  }).then(function (productId) {
    return getProductForms({
      apiQuotingUrl: apiQuotingUrl,
      productId: productId,
      compact: true
    }).then(function (productForms) {
      return {
        productForms: productForms,
        productId: productId
      };
    });
  }).then(function (_ref2) {
    var productForms = _ref2.productForms,
        productId = _ref2.productId;
    return deriveAvailableActions(productForms, featureFlags, debug).map(function (action) {
      return _extends({}, action, {
        productId: productId
      });
    });
  });
}
/**
 * Take a list of policy actions from the quoting API and compare those against
 * the launch darkly flags to determine which actions are available for a given
 * user in a given context.
 * @param {Array.<{
 *   action: String,
 *   label: String
 *   metadata: {{
 *     ActionAvailable: ("true"|"false"|undefined)
 *     RulesetsReady: ("true"|"false"|undefined)
 *   }}
 * }>} actions
 * @param {Object} featureFlags - See parameter in `getPacActions`
 * @param {Boolean} debug - Whether to show debugging log in console.
 * @returns {Array.<{
 *   policyActionId: String,
 *   policyActionLabel: String,
 *   enableRulesets: Boolean
 * }>} - Data structure expected by consuming client. Note that the key names
 *   match to props expected by <Pac />
 */

export function deriveAvailableActions(actions, featureFlags, debug) {
  if (debug === void 0) {
    debug = false;
  }

  // Garbage in, garbage out
  if (!(Array.isArray(actions) && typeof featureFlags === "object" && featureFlags !== null)) {
    return [];
  } // Look through each of our actions and see if it is considered available
  // by both the product and the feature flags from launch darkly.
  // If it is, we'll return the data structure we want to give back to the
  // consumer of Pac.


  var availableActions = actions.reduce(function (acc, action) {
    var name = action.name,
        label = action.label,
        _action$metadata = action.metadata;
    _action$metadata = _action$metadata === void 0 ? {} : _action$metadata;
    var ActionAvailable = _action$metadata.ActionAvailable,
        RulesetsReady = _action$metadata.RulesetsReady,
        ProductType = _action$metadata.ProductType;
    var flags = featureFlags["pac-" + (ProductType === "BOP" ? "bop-" : "") + name];

    if (ActionAvailable === "true" && Array.isArray(flags) && flags.indexOf(FF_ACTION) !== -1) {
      acc.push({
        policyActionId: name,
        policyActionLabel: label,
        enableRulesets: RulesetsReady === "true" && flags.indexOf(FF_RULESETS) !== -1
      });
    }

    return acc;
  }, []); // Log some useful info when applicable

  if (debug) {
    // eslint-disable-next-line no-console
    console.log("Pac actions matrix:"); // eslint-disable-next-line no-console

    if (console.table) {
      // eslint-disable-next-line no-console
      console.table(actions.reduce(function (acc, action) {
        var _extends2;

        var availableAction = availableActions.find(function (_ref3) {
          var policyActionId = _ref3.policyActionId;
          return policyActionId === action.name;
        });
        return _extends({}, acc, (_extends2 = {}, _extends2[action.name] = {
          "Action On?": Boolean(availableAction),
          "Rulesets On?": Boolean(availableAction) && availableAction.enableRulesets,
          "Product values": (action.metadata && action.metadata.ActionAvailable ? "ActionAvailable=" + action.metadata.ActionAvailable : "") + (action.metadata && action.metadata.RulesetsReady ? " RulesetsReady=" + action.metadata.RulesetsReady : ""),
          "Feature flag values": featureFlags["pac-" + action.name] ? featureFlags["pac-" + action.name].join(" ") : undefined
        }, _extends2));
      }, {}));
    }
  }

  return availableActions;
}